import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Calendar from 'react-calendar';
import { parseIsoDate, toIsoDate } from './ecxDateUtils';

function buildRangeValue(startValue, endValue) {
    const start = parseIsoDate(startValue);
    const end = parseIsoDate(endValue);

    if (start && end) {
        return [start, end];
    }

    if (start) {
        return start;
    }

    return null;
}

function EcxCalendarPopup({
    value,
    startValue = '',
    endValue = '',
    selectRange = false,
    onSelect,
    onRangeSelect,
    onClose,
    min,
    max,
    anchorRef,
}) {
    const popupRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [localValue, setLocalValue] = useState(() =>
        selectRange ? buildRangeValue(startValue, endValue) : parseIsoDate(value)
    );

    const updatePosition = () => {
        const anchor = anchorRef?.current;
        const popup = popupRef.current;

        if (!anchor || !popup) {
            return;
        }

        const anchorRect = anchor.getBoundingClientRect();
        const popupRect = popup.getBoundingClientRect();
        const viewportPadding = 12;

        let top = anchorRect.bottom + 6;
        let left = anchorRect.left;

        if (left + popupRect.width > window.innerWidth - viewportPadding) {
            left = window.innerWidth - popupRect.width - viewportPadding;
        }

        if (left < viewportPadding) {
            left = viewportPadding;
        }

        if (top + popupRect.height > window.innerHeight - viewportPadding) {
            top = anchorRect.top - popupRect.height - 6;
        }

        if (top < viewportPadding) {
            top = viewportPadding;
        }

        setPosition({ top, left });
    };

    useLayoutEffect(() => {
        updatePosition();
    }, [anchorRef, localValue]);

    useEffect(() => {
        if (!anchorRef?.current) {
            return undefined;
        }

        const handleReposition = () => updatePosition();
        window.addEventListener('resize', handleReposition);
        window.addEventListener('scroll', handleReposition, true);

        return () => {
            window.removeEventListener('resize', handleReposition);
            window.removeEventListener('scroll', handleReposition, true);
        };
    }, [anchorRef]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose?.();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const minDate = min ? parseIsoDate(min) : undefined;
    const maxDate = max ? parseIsoDate(max) : undefined;

    const awaitingEndDate =
        selectRange &&
        ((localValue instanceof Date && !(Array.isArray(localValue))) ||
            (Array.isArray(localValue) && localValue[0] && !localValue[1]));

    const resolveSingleDay = (candidate) => {
        if (candidate instanceof Date) {
            return candidate;
        }

        if (Array.isArray(candidate) && candidate[0] instanceof Date) {
            return candidate[0];
        }

        return null;
    };

    const handleChange = (nextValue) => {
        if (selectRange) {
            if (Array.isArray(nextValue) && nextValue[0] && nextValue[1]) {
                const [first, second] = nextValue;
                const start = first <= second ? first : second;
                const end = first <= second ? second : first;
                setLocalValue([start, end]);
                onRangeSelect?.(toIsoDate(start), toIsoDate(end));
                onClose?.();
                return;
            }

            const singleDay = resolveSingleDay(nextValue);
            if (singleDay) {
                setLocalValue(singleDay);
                // One date only for now — caller can search that day, or pick an end date next.
                onRangeSelect?.(toIsoDate(singleDay), '');
            }

            return;
        }

        if (!(nextValue instanceof Date)) {
            return;
        }

        onSelect?.(toIsoDate(nextValue));
        onClose?.();
    };

    const handleUseSingleDay = () => {
        const singleDay = resolveSingleDay(localValue);
        if (!singleDay) {
            return;
        }

        onRangeSelect?.(toIsoDate(singleDay), '');
        onClose?.();
    };

    const handleToday = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (minDate && today < minDate) {
            return;
        }

        if (maxDate && today > maxDate) {
            return;
        }

        if (selectRange) {
            // Today alone = single-day filter; user can reopen to extend into a range.
            setLocalValue(today);
            onRangeSelect?.(toIsoDate(today), '');
            onClose?.();
            return;
        }

        onSelect?.(toIsoDate(today));
        onClose?.();
    };

    const title = selectRange
        ? awaitingEndDate
            ? 'Select end date (optional)'
            : 'Select date or range'
        : 'Select date';

    return (
        <div
            ref={popupRef}
            className={`ecx-calendar-popup${selectRange ? ' ecx-calendar-popup--range' : ''}`}
            style={{ top: position.top, left: position.left }}
            role="dialog"
            aria-modal="true"
            aria-label={title}
        >
            <div className="ecx-calendar-popup__header">
                <div className="ecx-calendar-popup__title">
                    <i className="bi bi-calendar3"></i>
                    <span>{title}</span>
                </div>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__close-btn flex-shrink-0"
                    onClick={onClose}
                    aria-label="Close calendar"
                >
                    <i className="bi bi-x-lg"></i>
                </button>
            </div>

            <div className="ecx-calendar-popup__body">
                <Calendar
                    className="ecx-calendar"
                    selectRange={selectRange}
                    allowPartialRange={selectRange}
                    onChange={handleChange}
                    value={localValue}
                    minDate={minDate}
                    maxDate={maxDate}
                    prev2Label={null}
                    next2Label={null}
                    prevLabel={<i className="bi bi-chevron-left"></i>}
                    nextLabel={<i className="bi bi-chevron-right"></i>}
                    showNeighboringMonth={false}
                />
            </div>

            <div className="ecx-calendar-popup__footer">
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__footer-close-btn"
                    onClick={onClose}
                >
                    <i className="bi bi-x-circle me-1"></i>
                    Cancel
                </button>
                {awaitingEndDate ? (
                    <button
                        type="button"
                        className="btn btn-sm btn-accent rounded-pill"
                        onClick={handleUseSingleDay}
                    >
                        <i className="bi bi-check2 me-1"></i>
                        Use this day
                    </button>
                ) : (
                    <button type="button" className="btn btn-sm btn-accent rounded-pill" onClick={handleToday}>
                        <i className="bi bi-calendar-check me-1"></i>
                        Today
                    </button>
                )}
            </div>
        </div>
    );
}

export default EcxCalendarPopup;
